import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  Edge,
  EdgeChange,
  MarkerType,
  Node,
  NodeChange,
  NodeDragHandler,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  useReactFlow,
} from 'reactflow';

import { QueryKeys } from '@frontend/features/common/types';
import { UpdateWorkflowDto, WorkflowEdgeDto } from '@bandido/shared/dtos';
import { WorkflowEdgeType, WorkflowStepType } from '@bandido/shared/constants';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { useUpdateWorkflowMutation, useGetWorkflowById } from '../../queries';
import { WorkflowStepNode } from '../../types';
import { parseSteps } from './utils';

export type Field = 'comment' | 'template';

type WorkflowContextState = {
  steps: Node<WorkflowStepNode>[];
  edges: Edge[];
  setSteps: (nodes: Node<WorkflowStepNode>[]) => void;
  setEdges: (edges: Edge[]) => void;
  selectedStep: Node<WorkflowStepNode> | null;
  setSelectedStepId: (stepId: string | null) => void;
  setHasWorkflowChanges: (value: boolean) => void;
  onStepChange: (node: Partial<Node<WorkflowStepNode>>) => void;
  onDeleteStep: (id: string) => void;
  onDeleteEdge: (id: string) => void;
  onCreateStep: (event: any) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  cancelStep: Node<WorkflowStepNode> | null;
  onStepDragStop: NodeDragHandler;
  onEditStepField: (id: string, field: Field, value: string) => void;
  saveWorkflow: () => void;
  isUpdating: boolean;
  isLoadingWorkflow: boolean;
  hasWorkflowChanges: boolean;
  hasInitialStep: boolean;
};

export const WorkflowContext = createContext<WorkflowContextState>({
  steps: [],
  edges: [],
  setEdges: () => {},
  setSteps: () => {},
  setHasWorkflowChanges: () => {},
  onConnect: () => {},
  onStepDragStop: () => {},
  onEdgesChange: () => {},
  selectedStep: null,
  setSelectedStepId: () => {},
  onNodesChange: () => {},
  onStepChange: () => {},
  onDeleteStep: () => {},
  onDeleteEdge: () => {},
  onCreateStep: () => {},
  onEditStepField: () => {},
  saveWorkflow: () => {},
  isUpdating: false,
  isLoadingWorkflow: false,
  hasWorkflowChanges: false,
  hasInitialStep: false,
  cancelStep: null,
});

const WorkflowContextProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const reactFlowInstance = useReactFlow();

  const [steps, setSteps] = useState<Node<WorkflowStepNode>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [hasWorkflowChanges, setHasWorkflowChanges] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { workflowId } = useParams();
  const { data, isLoading: isLoadingWorkflow } = useGetWorkflowById(workflowId);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setSteps((nds) => applyNodeChanges(changes, nds));
    },
    [setSteps],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges],
  );

  const { mutate: updateWorkflow, isLoading: isUpdating } = useUpdateWorkflowMutation(String(workflowId), {
    mutationKey: [QueryKeys.updateWorkflow],
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.getWorkflowById);
      setHasWorkflowChanges(false);
      addToast({
        color: 'success',
        message: t('workflows.edit.success'),
      });
    },
  });

  const onDeleteStep = useCallback(
    (id: string) => {
      setSteps((prevSteps) => prevSteps.filter((step) => step.id !== id));
      setHasWorkflowChanges(true);
    },
    [setSteps],
  );

  const onDeleteEdge = useCallback(
    (id: string) => {
      setEdges((prevEdges) => prevEdges.filter((edge) => edge.id !== id));
      setHasWorkflowChanges(true);
    },
    [setEdges],
  );

  const onStepChange = useCallback(
    (updatedStep: Partial<Node>) => {
      setSteps((prevSteps) =>
        prevSteps.map((step) => {
          if (step.id !== updatedStep.id) {
            return step;
          } else {
            const newNode = { ...step, ...updatedStep };
            return newNode;
          }
        }),
      );
      setHasWorkflowChanges(true);
    },
    [setSteps],
  );

  const selectedStep = useMemo(() => steps.find((step) => step.id === selectedStepId) ?? null, [steps, selectedStepId]);

  const onEditStepField = useCallback(
    (id: string, field: Field, value: string) => {
      setSteps((prevSteps) =>
        prevSteps.map((step) => {
          if (step.id === id) {
            return { ...step, data: { ...step.data, [field]: value } };
          } else {
            return step;
          }
        }),
      );
      setHasWorkflowChanges(true);
    },
    [setSteps],
  );

  const cancelStep = useMemo(() => steps.find((step) => step.data.isCancelStep) ?? null, [steps]);

  const hasInitialStep = useMemo(() => steps.some((step) => step.type === WorkflowStepType.INITIAL), [steps]);

  const onStepDragStop = (_event: React.MouseEvent, { data, position, type, dragging }: Node<WorkflowStepNode>) => {
    if (!dragging) return;

    const stepToUpdate = steps.find((step) => step.id === data.id);

    onStepChange({
      ...stepToUpdate,
      data: {
        ...stepToUpdate?.data,
        position,
        type,
      },
    });
  };

  const onConnect = useCallback(
    (connection: Connection) => {
      const { color } = data?.steps.find((step) => step.id === connection.source) || { color: '#000000' };
      const edge = {
        ...connection,
        type: WorkflowEdgeType.CUSTOM,
        style: {
          strokeWidth: 2,
          stroke: color,
        },
        markerEnd: {
          width: 12,
          height: 12,
          type: MarkerType.ArrowClosed,
          color,
        },
        animated: false,
        id: `${connection.source}-${connection.target}`,
      };
      setHasWorkflowChanges(true);

      setEdges((prevEdges) => {
        const newEdges = addEdge(edge, prevEdges);
        return newEdges;
      });
    },
    [data?.steps, setEdges],
  );

  const onCreateStep = useCallback(
    (event: any) => {
      event.preventDefault();

      const type: WorkflowStepType = event.dataTransfer.getData('application/reactflow');

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newStep: Node<WorkflowStepNode> = {
        id: `${Date.now()}`,
        position,
        type,
        data: {
          tmpId: `${Date.now()}`,
          id: `${Date.now()}`,
          color: '#000000',
          name: 'New Step',
          teams: [],
          isCancelStep: false,
        },
      };
      setSteps((prevSteps) => [...prevSteps, newStep]);
      setHasWorkflowChanges(true);
    },
    [setSteps, reactFlowInstance],
  );

  const saveWorkflow = useCallback(async () => {
    if (!data?.id) return;
    try {
      const workflow: UpdateWorkflowDto = {
        edges: edges as WorkflowEdgeDto[],
        steps: steps.map((step) => ({
          id: step.data.id,
          tmpId: step.data.tmpId,
          color: step.data.color,
          comment: step.data.comment,
          template: step.data.template,
          isCancelStep: step.data.isCancelStep,
          data: {
            position: step.position,
            type: step.type as WorkflowStepType,
          },
          name: step.data.name,
          teamIds: step.data.teams,
          workflowId: data.id,
        })),
      };
      updateWorkflow(workflow);
    } catch (_e) {
      addToast({
        color: 'success',
        message: t('workflows.edit.error'),
      });
    }
  }, [data?.id, updateWorkflow, steps, edges]);

  const handleBeforeUnload = useCallback(
    async (event: any) => {
      if (hasWorkflowChanges) {
        event.preventDefault();
        event.returnValue = '';

        const confirmLeave = window.confirm('Hay cambios sin guardar. ¿Deseas guardarlos antes de salir?');

        if (confirmLeave) {
          await saveWorkflow();
          window.removeEventListener('beforeunload', handleBeforeUnload);
          window.location.reload();
        }
      }
    },
    [hasWorkflowChanges, saveWorkflow],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  useEffect(() => {
    if (data?.steps) {
      const workflowSteps: Node<WorkflowStepNode>[] = parseSteps(data?.steps);
      setSteps(workflowSteps);
    }
    if (data?.edges) {
      setEdges(JSON.parse(data?.edges));
    }
  }, [data?.steps, data?.edges]);

  useEffect(
    () => () => {
      setSteps([]);
      setEdges([]);
    },
    [],
  );

  return (
    <WorkflowContext.Provider
      value={{
        steps,
        edges,
        setEdges,
        setSteps,
        setHasWorkflowChanges,
        onConnect,
        hasInitialStep,
        onNodesChange,
        onEdgesChange,
        selectedStep,
        setSelectedStepId,
        onStepDragStop,
        onStepChange,
        onDeleteStep,
        onDeleteEdge,
        onCreateStep,
        onEditStepField,
        saveWorkflow,
        isUpdating,
        isLoadingWorkflow,
        hasWorkflowChanges,
        cancelStep,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

export default WorkflowContextProvider;
